import PropTypes from 'prop-types';
import React from 'react';
import { Button, CheckBoxField, ConsentTypeChoice, InputField, RadioField } from '@unite-us/app-components';

import {
  validateEmailAddress,
  validatePhoneNumber,
} from 'src/components/InformedConsent/utils/validations';
import { CONSENT_OPTIONS } from 'src/components/InformedConsent/constants';
import { env } from 'src/config/env';
import ConsentFileUpload from './ConsentFileUpload';
import DownloadConsentFormLink from './DownloadConsentFormLink';
import ViewVerbalConsentScriptLink from './ViewVerbalConsentScriptLink';

const BOOL_OPTIONS = [
  { label: 'Yes', value: 'accepted' },
  { label: 'No', value: 'declined' },
];

const ConsentTypeChoices = ({
  fields,
  registerField,
  onSubmit,
  selectConsentType,
  selectedConsentType,
  showConsent,
  setConsentAttestation,
  showNHConsent,
  sentReferrals,
  hasPlatformConsent,
}) => (
  <div className="consent-type-choices">
    {!showNHConsent && (
      <div className="consent-type-choices-group attestation-consent">
        <h3 className="consent-type-choices-group__title">
          User Attestation
        </h3>
        <ConsentTypeChoice
          type={CONSENT_OPTIONS.ON_SCREEN_ATTESTATION}
          label="On-Screen Consent by Attestation (Client Consented Verbally)"
          onSelect={selectConsentType}
          selectedType={selectedConsentType}
        >
          <Button
            id="go-to-form-btn"
            className="consent-go-to-form-btn h-9 whitespace-no-wrap"
            label="Go To Form"
            primary
            onClick={() => setConsentAttestation(true)}
          />
        </ConsentTypeChoice>
      </div>
    )}
    <div className="consent-type-choices-group">
      <h3 className="consent-type-choices-group__title">
        Digital Consent
      </h3>
      <div className="flex flex-col gap-2">
        <ConsentTypeChoice
          type={CONSENT_OPTIONS.PHONE_NUMBER}
          label="Send request for signature by text message (SMS)"
          onSelect={selectConsentType}
          selectedType={selectedConsentType}
        >
          <div className="flex items-start gap-x-8 gap-y-4 flex-wrap">
            <div className="flex-auto" style={{ flexGrow: 2 }}>
              <InputField
                ref={registerField}
                id="phone-number"
                label="Phone Number"
                field={fields[CONSENT_OPTIONS.PHONE_NUMBER]}
                hideLabel
                disabled={selectedConsentType !== CONSENT_OPTIONS.PHONE_NUMBER}
                mask="(999) 999-9999"
                validations={{ func: validatePhoneNumber }}
                style={{ input: { margin: '0' } }}
              />
            </div>
            <div className="flex-1 flex-shrink-0">
              <Button
                id="consent-submit-phone-btn"
                className="consent-submit-request-by-phone-btn h-9 whitespace-no-wrap"
                label="Submit"
                onClick={onSubmit}
                primary
              />
            </div>
          </div>
        </ConsentTypeChoice>

        <ConsentTypeChoice
          type={CONSENT_OPTIONS.EMAIL_ADDRESS}
          label="Send request for signature by Email"
          onSelect={selectConsentType}
          selectedType={selectedConsentType}
        >
          <div className="flex items-start gap-x-8 gap-y-4 flex-wrap">
            <div className="flex-auto" style={{ flexGrow: 2 }}>
              <InputField
                ref={registerField}
                id="email-address"
                label="Email Address"
                field={fields[CONSENT_OPTIONS.EMAIL_ADDRESS]}
                hideLabel
                disabled={selectedConsentType !== CONSENT_OPTIONS.EMAIL_ADDRESS}
                validations={{ func: validateEmailAddress }}
                style={{ input: { margin: '0' } }}
              />
            </div>
            <div className="flex-1 flex-shrink-0">
              <Button
                id="consent-submit-email-btn"
                className="consent-submit-request-by-email-btn h-9 whitespace-no-wrap"
                label="Submit"
                onClick={onSubmit}
                primary
              />
            </div>
          </div>
        </ConsentTypeChoice>

        <ConsentTypeChoice
          type={CONSENT_OPTIONS.ON_SCREEN}
          label="On-Screen Consent"
          onSelect={selectConsentType}
          selectedType={selectedConsentType}
        >
          <Button
            id="go-to-form-btn"
            className="consent-go-to-form-btn h-9 whitespace-no-wrap"
            label="Go To Form"
            primary
            onClick={showConsent}
          />
        </ConsentTypeChoice>
      </div>
    </div>

    <div className="consent-type-choices-group paper-and-verbal-consent">
      <h3 className="consent-type-choices-group__title">
        Paper And Verbal Consent
      </h3>
      <div className="consent-type-choices-group__choices">
        <ConsentTypeChoice
          type={CONSENT_OPTIONS.DOCUMENT}
          label="Upload signed paper form"
          onSelect={selectConsentType}
          selectedType={selectedConsentType}
          dropzone={showNHConsent ? (
            <div className="flex flex-col mb-8 space-y-6">
              <div className="border-b border-solid border-light-border-blue" />
              {!hasPlatformConsent && (
                <>
                  <div className="flex justify-between">
                    <span className={fields[CONSENT_OPTIONS.DOCUMENT]?.invalid ? 'text-red' : ''}>
                      Unite Us Consent <span className="text-red">*Required</span>
                    </span>
                    <DownloadConsentFormLink
                      text="Download Unite Us consent form"
                      url={env.CONSENT_DOWNLOAD_URL}
                    />
                  </div>
                  <ConsentFileUpload name="Consent" type={CONSENT_OPTIONS.DOCUMENT} />
                </>
              )}
              {(showNHConsent && sentReferrals.length > 0) && (
                <>
                  <div className="flex justify-between">
                    <span className={fields[CONSENT_OPTIONS.NH_DOCUMENT]?.invalid ? 'text-red' : ''}>
                      New Hampshire Consent <span className="text-red">*Required</span>
                    </span>
                    <DownloadConsentFormLink
                      text="Download New Hampshire consent form"
                      url={env.NH_CONSENT_DOWNLOAD_URL}
                    />
                  </div>
                  <ConsentFileUpload name="NHConsent" type={CONSENT_OPTIONS.DOCUMENT} />
                  <div className="flex flex-col space-y-6">
                    <p>Discretely document the client&apos;s responses to the New Hampshire consent form below.</p>
                    <div className="flex flex-col space-y-4">
                      <p>
                        Did the client agree to share referral-specific information with a
                        Network of health and social service partners powered by Unite Us software?
                        Select which of the following referrals your client consented to sharing information about.
                      </p>
                      <div className="flex flex-col space-y-4">
                        {sentReferrals.map(({ serviceName, programNames }, index) => (
                          <CheckBoxField
                            field={fields[CONSENT_OPTIONS.NH_DOCUMENT_REFERRALS][index]}
                            label={`Referral for ${serviceName}: ${programNames.join(', ')}`}
                            showError={false}
                            showHint={false}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col space-y-4">
                      <RadioField
                        id="referral_specific_radio"
                        labelClassName="text-sm font-regular-font mb-4"
                        label={(
                          <>
                            Did the client consent to sharing referral-specific information?{' '}
                            <span className="text-red">*Required</span>
                          </>
                        )}
                        field={{
                          ...fields[CONSENT_OPTIONS.NH_DOCUMENT_REFERRAL_SPECIFIC],
                          invalid: !fields[CONSENT_OPTIONS.NH_DOCUMENT_REFERRAL_SPECIFIC].value,
                        }}
                        options={BOOL_OPTIONS}
                      />
                    </div>
                    <div className="flex flex-col space-y-4">
                      <RadioField
                        id="state_share_radio"
                        labelClassName="text-sm font-regular-font mb-4"
                        label={(
                          <>
                            Did the client consent to sharing their information with the state of New Hampshire?{' '}
                            <span className="text-red">*Required</span>
                          </>
                        )}
                        field={{
                          ...fields[CONSENT_OPTIONS.NH_DOCUMENT_STATE_SHARE],
                          invalid: !fields[CONSENT_OPTIONS.NH_DOCUMENT_STATE_SHARE].value,
                        }}
                        options={BOOL_OPTIONS}
                      />
                    </div>
                  </div>
                  <div className="border-b border-solid border-light-border-blue" />
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col mb-8 space-y-6">
              <div className="flex justify-between">
                <span>Unite Us Consent</span>
                <DownloadConsentFormLink
                  text="Download Consent Form"
                  url={env.CONSENT_DOWNLOAD_URL}
                />
              </div>
              <ConsentFileUpload name="Consent" type={CONSENT_OPTIONS.DOCUMENT} />
            </div>
          )}
        />

        {!showNHConsent && (
          <ConsentTypeChoice
            type={CONSENT_OPTIONS.AUDIO}
            label="Upload audio file (client consented verbally)"
            onSelect={selectConsentType}
            selectedType={selectedConsentType}
            dropzone={(
              <ConsentFileUpload name="Consent" type={CONSENT_OPTIONS.AUDIO} />
            )}
          >
            <ViewVerbalConsentScriptLink />
          </ConsentTypeChoice>
        )}
      </div>
    </div>
  </div>
);

ConsentTypeChoices.propTypes = {
  fields: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  registerField: PropTypes.func.isRequired,
  setConsentAttestation: PropTypes.func.isRequired,
  selectConsentType: PropTypes.func.isRequired,
  selectedConsentType: PropTypes.string.isRequired,
  showConsent: PropTypes.func.isRequired,
  showNHConsent: PropTypes.bool,
  sentReferrals: PropTypes.array,
  hasPlatformConsent: PropTypes.bool,
};

ConsentTypeChoices.defaultProps = {
  showNHConsent: false,
  sentReferrals: [],
  hasPlatformConsent: false,
};

export default ConsentTypeChoices;
